import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar as CalendarIcon, Clock, Target, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { cadenceLabel } from "../lib/cadence";
import AddToCalendar from "../components/AddToCalendar";
import RenameMeeting from "../components/RenameMeeting";
import SignOffPopover from "../components/SignOffPopover";

function dueLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  const diff = Math.round((day - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, tone: "danger" };
  if (diff === 0) return { label: "Due today", tone: "gold" };
  if (diff === 1) return { label: "Due tomorrow", tone: "gold" };
  if (diff < 7) return { label: `In ${diff}d`, tone: "muted" };
  return { label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), tone: "muted" };
}

/** One docket row — rename in place, change the due date, sign off. */
function DocketRow({ item, due, onRename, onDue, onSignOff }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title);

  const commit = () => {
    setEditing(false);
    onRename(title);
  };

  return (
    <li className="surface rounded-md p-4" data-testid={`calendar-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <Clock size={14} className="text-[#8a8a8a] shrink-0 mt-1" />
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") { setTitle(item.title); setEditing(false); }
              }}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-2 py-1 text-sm text-[#eaeaea] focus:outline-none focus:border-[#c89f45]"
              data-testid={`docket-title-input-${item.id}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="group flex items-center gap-2 text-left w-full"
              data-testid={`docket-edit-${item.id}`}
            >
              <span className="text-sm truncate">{item.title}</span>
              <Pencil size={11} className="text-[#555] group-hover:text-[#c89f45] transition-colors shrink-0" />
            </button>
          )}
          <div className="flex items-center gap-3 mt-3">
            <input
              type="date"
              value={(item.due_date || "").slice(0, 10)}
              onChange={(e) => onDue(e.target.value)}
              className="bg-[#0a0a0a] border border-[#2a2a2a] rounded px-2 py-1 text-xs font-mono text-[#bbb] focus:outline-none focus:border-[#c89f45]"
              data-testid={`docket-due-${item.id}`}
            />
            {due && (
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${
                due.tone === "danger" ? "bg-red-900/30 text-red-300" :
                due.tone === "gold" ? "bg-[#c89f45]/20 text-[#c89f45]" :
                "bg-[#1a1a1a] text-[#888]"
              }`}>{due.label}</span>
            )}
            <SignOffPopover item={item} onConfirm={onSignOff} />
          </div>
        </div>
      </div>
    </li>
  );
}

export default function CalendarPage() {
  const [data, setData] = useState({ meetings: [], action_items: [] });
  const [loading, setLoading] = useState(true);
  // Due date defaults to today — the calendar feed only carries dated items.
  const [draft, setDraft] = useState({ title: "", due_date: new Date().toISOString().slice(0, 10) });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/calendar").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  // Request notification permission once, then fire today-due notifications.
  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
    if (Notification.permission === "granted" && data.action_items?.length) {
      const today = new Date().toDateString();
      data.action_items.forEach((it) => {
        if (it.due_date && new Date(it.due_date).toDateString() === today && !sessionStorage.getItem(`notif-${it.id}`)) {
          new Notification("Resolution due today", { body: it.title });
          sessionStorage.setItem(`notif-${it.id}`, "1");
        }
      });
    }
  }, [data]);

  const upcoming = (data.meetings || []).filter((m) => m.scheduled_for && new Date(m.scheduled_for) >= new Date(Date.now() - 86400000));
  // Signed-off resolutions drop off the docket — this list is what's still owed.
  const items = (data.action_items || []).filter((i) => i.status !== "done");

  const replaceItem = (next) =>
    setData((d) => ({ ...d, action_items: (d.action_items || []).map((x) => (x.id === next.id ? next : x)) }));

  const addItem = async () => {
    const title = draft.title.trim();
    if (!title) return;
    setAdding(true);
    try {
      const res = await api.post("/action-items", { title, due_date: draft.due_date || null });
      setData((d) => ({ ...d, action_items: [...(d.action_items || []), res.data] }));
      setDraft({ title: "", due_date: new Date().toISOString().slice(0, 10) });
      toast.success("Resolution added to the docket");
    } catch {
      toast.error("Could not add this resolution");
    } finally {
      setAdding(false);
    }
  };

  const renameItem = async (item, title) => {
    const clean = (title || "").trim();
    if (!clean || clean === item.title) return;
    try {
      const res = await api.patch(`/action-items/${item.id}`, { title: clean });
      replaceItem(res.data);
      toast.success("Resolution renamed");
    } catch {
      toast.error("Could not rename this resolution");
    }
  };

  const setItemDue = async (item, date) => {
    try {
      const res = await api.patch(`/action-items/${item.id}`, { due_date: date || null });
      replaceItem(res.data);
      toast.success(date ? "Due date updated" : "Due date cleared");
    } catch {
      toast.error("Could not set the due date");
    }
  };

  const signOffItem = async (item, completedOn) => {
    try {
      const res = await api.post(`/action-items/${item.id}/acknowledge`, { completed_on: completedOn });
      replaceItem(res.data);
      const stamped = new Date(completedOn + "T00:00:00");
      toast.success(`Signed off · ${stamped.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/action-items/${item.id}/acknowledge`);
              const back = await api.patch(`/action-items/${item.id}`, { status: "pending" });
              replaceItem(back.data);
            } catch {
              toast.error("Could not undo the sign-off");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not sign off");
    }
  };

  const updateMeeting = (next, opts) => {
    if (opts?.refetch) {
      api.get("/calendar").then((r) => setData(r.data)).catch(() => {});
      return;
    }
    setData((d) => ({ ...d, meetings: (d.meetings || []).map((x) => (x.id === next.id ? next : x)) }));
  };

  return (
    <div className="space-y-12" data-testid="calendar-root">
      <header>
        <p className="label-eyebrow">Schedule</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Calendar</h1>
        <p className="text-[#888] mt-4 max-w-xl">Upcoming sessions and resolutions on the docket. Browser notifications fire on the day an item is due.</p>
      </header>

      {loading ? (
        <div className="surface rounded-md p-8 text-sm text-[#8a8a8a]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl flex items-center gap-3">
                <CalendarIcon size={18} className="text-[#c89f45]" /> Upcoming sessions
              </h2>
              <Link to="/meetings/new" className="text-xs text-[#888] hover:text-[#c89f45]" data-testid="schedule-session">
                Schedule →
              </Link>
            </div>
            {upcoming.length === 0 ? (
              <div className="surface rounded-md p-8 text-sm text-[#8a8a8a]" data-testid="empty-upcoming">
                No sessions scheduled. Set <span className="font-mono text-[#888]">scheduled_for</span> when creating a session to populate this calendar.
              </div>
            ) : (
              <ul className="space-y-2">
                {upcoming.map((m) => (
                  <li key={m.id} className="surface rounded-md p-5" data-testid={`upcoming-${m.id}`}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="min-w-0">
                        <p className="label-eyebrow">{cadenceLabel(m.cadence)} · {m.meeting_type}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Link to={`/meetings/${m.id}`} className="font-serif text-lg hover:text-[#c89f45] transition-colors truncate" data-testid={`upcoming-link-${m.id}`}>
                            {m.title}
                          </Link>
                          <RenameMeeting meeting={m} compact onUpdated={updateMeeting} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 sm:flex-col sm:items-end shrink-0">
                        <span className="font-mono text-xs text-[#c89f45]">
                          {new Date(m.scheduled_for).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                        <AddToCalendar meeting={m} compact onUpdated={updateMeeting} />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="font-serif text-2xl flex items-center gap-3 mb-5">
              <Target size={18} className="text-[#c89f45]" /> Resolutions on the docket
            </h2>

            {/* Quick add — put something on the docket without opening a session. */}
            <div className="surface rounded-md p-4 mb-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="docket-quick-add">
              <input
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
                placeholder="Add a resolution…"
                className="flex-1 min-w-0 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-[#eaeaea] placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#c89f45]"
                data-testid="docket-add-title"
              />
              <input
                type="date"
                value={draft.due_date}
                onChange={(e) => setDraft((d) => ({ ...d, due_date: e.target.value }))}
                className="bg-[#0a0a0a] border border-[#2a2a2a] rounded px-2 py-2 text-xs font-mono text-[#bbb] focus:outline-none focus:border-[#c89f45]"
                data-testid="docket-add-due"
              />
              <button
                onClick={addItem}
                disabled={!draft.title.trim() || adding}
                className="shrink-0 inline-flex items-center gap-1.5 rounded bg-[#c89f45] px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#0a0a0a] hover:bg-[#d4ac52] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="docket-add-submit"
              >
                <Plus size={12} /> {adding ? "Adding…" : "Add"}
              </button>
            </div>
            {items.length === 0 ? (
              <div className="surface rounded-md p-8 text-sm text-[#8a8a8a]" data-testid="empty-items-with-due">
                Nothing outstanding with a due date. Signed-off resolutions drop off this docket.
              </div>
            ) : (
              <ul className="space-y-2">
                {items.map((it) => (
                  <DocketRow
                    key={it.id}
                    item={it}
                    due={dueLabel(it.due_date)}
                    onRename={(title) => renameItem(it, title)}
                    onDue={(date) => setItemDue(it, date)}
                    onSignOff={(date) => signOffItem(it, date)}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
